import { useCallback, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { Dataset, DatasetColumn, ReportDetail } from '@tallyframe/core';
import {
  useCreateDataset,
  useCreateReport,
  useCreateReportFromCsv,
  usePreviewCsv,
  useRefreshReport,
  useSubmitSnapshot,
  useSubmitSnapshotCsv
} from '../api/report-queries';
import { ApiError } from '../api/errors';
import type { CsvPreview } from '../api/types';
import { useSession } from '../app/session-context';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { DropZone } from '../ui/DropZone';
import { Banner, ErrorBanner } from '../components/ErrorBanner';
import { PlanLimitMessage } from '../components/PlanLimitMessage';
import { IssueList } from '../components/IssueList';
import { MAX_SNAPSHOT_JSON_BYTES, coerceCsvRows, estimateJsonBytes } from './csv-coerce';
import { deepEqual } from '../editing/diff';
import { ColumnStep, TYPE_LABEL } from './ColumnStep';

export { TYPE_LABEL };

const MUTED_TEXT = 'm-0 p-0 font-ui text-sm text-muted';

function defaultGrain(filename: string): string {
  return `One row per line of the uploaded file "${filename}".`;
}

interface PreviewState {
  file: File;
  text: string;
  preview: CsvPreview;
  inferred: DatasetColumn[];
}

/**
 * CSV upload: preview, correct, create.
 *
 * The server infers the schema once and returns it as a *suggestion* along with
 * whatever it was unsure about. Correcting it here matters more than it looks:
 * a column typed as text cannot be summed, and a column wrongly typed as a
 * number quietly drops the rows that did not parse. The person who has the file
 * open is the only one who knows which it should be.
 */
export function CsvUploadDialog(props: CsvUploadDialogProps): ReactElement {
  return props.target ? (
    <TargetUploadDialog
      target={props.target}
      onClose={props.onClose}
      onCreated={props.onCreated}
      upgradeUrl={props.upgradeUrl}
    />
  ) : (
    <CreateFromCsvDialog {...props} />
  );
}

export interface CsvUploadDialogProps {
  onClose(): void;
  /** Called with the new report, or, in target mode, the refreshed one. */
  onCreated(detail: ReportDetail): void;
  /** Where the plan-limit message's upgrade link points; null hides the link. */
  upgradeUrl: string | null;
  /** Fill an existing report's dataset instead of creating a new report. */
  target?: { dataset: Dataset; reportId: string };
}

function Failure(props: { error: unknown; upgradeUrl: string | null }): ReactElement | null {
  if (!props.error) return null;
  return ApiError.isApiError(props.error) && props.error.code === 'PLAN_LIMIT_EXCEEDED' ? (
    <PlanLimitMessage error={props.error} upgradeUrl={props.upgradeUrl} />
  ) : (
    <ErrorBanner error={props.error} />
  );
}

/**
 * Target mode: the report already exists and has never had data, so there is
 * no schema to review. The file goes to the dataset as-is (the server matches
 * headers by column id or label) and the report is pointed at it.
 */
function TargetUploadDialog(props: {
  target: { dataset: Dataset; reportId: string };
  onClose(): void;
  onCreated(detail: ReportDetail): void;
  upgradeUrl: string | null;
}): ReactElement {
  const workspaceId = useSession().snapshot.workspaceId ?? '';
  const submitCsv = useSubmitSnapshotCsv();
  const refresh = useRefreshReport(workspaceId);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Set once the file is in, so a retry after a failed refresh only refreshes.
  const uploaded = useRef<File | null>(null);

  const upload = useCallback(async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      if (uploaded.current !== file) {
        await submitCsv.mutateAsync({ datasetId: props.target.dataset.id, file });
        uploaded.current = file;
      }
      // The report has never had data, so its expected active snapshot is null.
      const detail = await refresh.mutateAsync({
        reportId: props.target.reportId,
        expectedActiveSnapshotId: null
      });
      props.onCreated(detail);
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }, [file, props, refresh, submitCsv]);

  return (
    <Dialog
      title="Upload data for this report"
      isOpen
      onOpenChange={(open) => {
        if (!open && !busy) props.onClose();
      }}
      footer={
        <>
          <Button onPress={props.onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} busyLabel="Uploading…" isDisabled={!file} onPress={() => void upload()}>
            Upload data
          </Button>
        </>
      }
    >
      <DropZone
        accept=".csv,text/csv"
        label="CSV file"
        onFile={(chosen) => {
          setFile(chosen);
          setError(null);
        }}
        onReject={() => setError(new Error('Choose a .csv file'))}
        disabled={busy}
      />
      <p className={MUTED_TEXT}>
        {file
          ? `Chosen: ${file.name}`
          : 'The first row must be the column headers, matching the columns listed on this page.'}
      </p>
      <Failure error={error} upgradeUrl={props.upgradeUrl} />
    </Dialog>
  );
}

function CreateFromCsvDialog(props: CsvUploadDialogProps): ReactElement {
  const workspaceId = useSession().snapshot.workspaceId ?? '';
  const previewCsv = usePreviewCsv();
  const createFromCsv = useCreateReportFromCsv(workspaceId);
  const createDataset = useCreateDataset();
  const submitSnapshot = useSubmitSnapshot();
  const createReport = useCreateReport(workspaceId);

  const [state, setState] = useState<PreviewState | null>(null);
  const [title, setTitle] = useState('');
  const [grain, setGrain] = useState('');
  const [columns, setColumns] = useState<DatasetColumn[]>([]);
  const [busy, setBusy] = useState<false | 'previewing' | 'creating'>(false);
  const [error, setError] = useState<unknown>(null);
  // Progress of a corrected create, so a retry resumes at the failed step.
  const progress = useRef<{ datasetId: string | null; snapshotDone: boolean }>({
    datasetId: null,
    snapshotDone: false
  });

  const pickFile = useCallback(
    async (file: File) => {
      setBusy('previewing');
      setError(null);
      try {
        const [preview, text] = await Promise.all([previewCsv.mutateAsync(file), file.text()]);
        setState({ file, text, preview, inferred: preview.columns });
        setColumns(preview.columns.map((column) => ({ ...column })));
        setTitle(preview.suggestedName);
        setGrain(defaultGrain(file.name));
        progress.current = { datasetId: null, snapshotDone: false };
      } catch (cause) {
        setState(null);
        setError(cause);
      } finally {
        setBusy(false);
      }
    },
    [previewCsv]
  );

  const rejectFile = useCallback(() => {
    setError(new Error('Choose a .csv file'));
  }, []);

  const patchColumn = useCallback((index: number, patch: Partial<DatasetColumn>) => {
    // The dataset and snapshot built from the old columns no longer apply.
    progress.current = { datasetId: null, snapshotDone: false };
    setColumns((current) =>
      current.map((column, position) => (position === index ? { ...column, ...patch } : column))
    );
  }, []);

  const create = useCallback(async () => {
    if (!state) return;
    setBusy('creating');
    setError(null);
    try {
      const untouched =
        deepEqual(columns, state.inferred) && grain === defaultGrain(state.file.name);

      if (untouched) {
        // Nothing to correct: let the server do the whole thing in one call.
        const detail = await createFromCsv.mutateAsync({
          file: state.file,
          title: title.trim() || state.preview.suggestedName
        });
        props.onCreated(detail);
        return;
      }

      const { rows } = coerceCsvRows(state.text, columns);
      const bytes = estimateJsonBytes(rows);
      if (bytes > MAX_SNAPSHOT_JSON_BYTES) {
        throw new ApiError(
          'LIMIT_EXCEEDED',
          `Applying your corrections means sending the rows as JSON, which comes to ${Math.round(bytes / 1_000_000)} MB — over the ${Math.round(MAX_SNAPSHOT_JSON_BYTES / 1_000_000)} MB request limit. Upload the file without corrections, or split it.`,
          413
        );
      }

      if (progress.current.datasetId === null) {
        const { dataset } = await createDataset.mutateAsync({
          dataset: {
            name: state.preview.suggestedName,
            grain: grain.trim() || defaultGrain(state.file.name),
            columns,
            sourceDescription: state.file.name
          }
        });
        progress.current.datasetId = dataset.id;
      }
      const datasetId = progress.current.datasetId;
      if (!progress.current.snapshotDone) {
        await submitSnapshot.mutateAsync({
          datasetId,
          input: { rows, sourceDescription: state.file.name }
        });
        progress.current.snapshotDone = true;
      }
      const detail = await createReport.mutateAsync({
        datasetId,
        title: title.trim() || state.preview.suggestedName
      });
      props.onCreated(detail);
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }, [columns, createDataset, createFromCsv, createReport, grain, props, state, submitSnapshot, title]);

  return (
    <Dialog
      title="New report from a CSV"
      size="lg"
      isOpen
      onOpenChange={(open) => {
        if (!open && busy === false) props.onClose();
      }}
      footer={
        <>
          <Button onPress={props.onClose} isDisabled={busy === 'creating'}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy === 'creating'}
            busyLabel="Creating…"
            isDisabled={!state || busy !== false}
            onPress={() => void create()}
          >
            Create report
          </Button>
        </>
      }
    >
      <DropZone
        accept=".csv,text/csv"
        label="CSV file"
        onFile={(file) => void pickFile(file)}
        onReject={rejectFile}
        disabled={busy !== false}
      />
      <p className={MUTED_TEXT}>
        The first row must be the column headers. Nothing is created until you press Create report.
      </p>

      <Failure error={error} upgradeUrl={props.upgradeUrl} />

      {busy === 'previewing' ? <p className={MUTED_TEXT}>Reading the file…</p> : null}

      {state ? (
        <>
          {state.preview.issues.length > 0 ? (
            <Banner tone="warn" title={`${state.preview.issues.length} thing(s) to check before creating this dataset`}>
              <IssueList issues={state.preview.issues} />
            </Banner>
          ) : null}

          <ColumnStep
            columns={columns}
            sampleRows={state.preview.sampleRows}
            title={title}
            grain={grain}
            onTitle={setTitle}
            onGrain={(value) => {
              progress.current = { datasetId: null, snapshotDone: false };
              setGrain(value);
            }}
            onColumn={patchColumn}
            columnsHint="Inferred from the file. Correct anything that is wrong."
            sampleCaption={`${Math.min(state.preview.sampleRows.length, 20)} of ${state.preview.totalRows.toLocaleString('en-US')}`}
          />
        </>
      ) : null}
    </Dialog>
  );
}
